import { Action } from 'redux';

import { APICallStatus } from '../../design-system/types';
import { DossierId } from '../dossier/types';
import { PhoneCall } from '../phoning/types';

import * as actions from './actions';

export enum MessageType {
    TravellerToAgent = 1,
    AgentToTraveller = 2,
    EvaneosNotificationToAgent = 12,
}

export enum MessageStatus {
    Read = 13,
}

export type MessageFromAPI = {
    id: number;
    quote_id: number;
    send_date: string;
    updated_at: string;
    type: MessageType;
    status: MessageStatus;
    body: string;
    body_html: string;
    message_id: string;
    provenance: string | null | undefined;
    author_id: number;
    author_firstname: string;
    author_lastname: string;
    author_picture_id: string | null | undefined;
    parsed: number;
    attachments: AttachmentFromAPI[];
};

// From discussion API
export type AttachmentFromAPI = {
    id: number;
    path: string;
    type: AttachmentTypeFromAPI;
    message_id: number;
};

// From API V2
export type MessageAttachmentFromAPI = {
    messageId: number | null;
    name: string;
    type: AttachmentTypeFromAPI;
    url: string;
};

export type AttachmentTypeFromAPI =
    | 'TipiProposalAttachement'
    | 'application/pdf'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'image/jpeg'
    | 'image/png'
    | 'text/plain';

export enum AttachmentType {
    TIPI = 'TipiProposalAttachement',
    PDF = 'pdf',
    DOCX = 'vnd.openxmlformats-officedocument.wordprocessingml.document',
    JPG = 'jpeg',
    PNG = 'png',
    TXT = 'plain',
}

export type MessageAttachment = {
    name: string;
    type: AttachmentType;
    id?: number;
    link?: string;
    messageId?: number | null;
};

export type Message = {
    id: number | string;
    quoteId: number;
    isFromTraveller: boolean;
    isFromAgent: boolean;
    isEvaneosNotification: boolean;
    sendDate: Date;
    updatedAt: Date;
    authorName: string;
    authorId: string;
    isMessageRead: boolean;
    body: string;
    htmlBody: string;
    attachments: Array<MessageAttachment>;
    messageIdFromAPI: string;
    // 'request-manager' | 'mobile' | 'mailgun'
    provenance?: string;
};

export type PaginationFromAPI = {
    count: number;
    current_page: number;
    total: number;
    total_pages: number;
    per_page?: number;
    links: { next?: string; previous?: string };
};

export type Pagination = {
    count: number;
    currentPage: number;
    total: number;
    totalPages: number;
    links: { next?: string; previous?: string };
};

export type SendingMessage = {
    id: string;
    content: string;
    contentHtml?: string;
};

export type Attachment = {
    file: File;
    category: string | null;
    id?: string;
};

export type AttachmentWithStatus = Attachment & {
    filenameId: string;
    status: APICallStatus;
};

export type AttachmentsById = Record<string, AttachmentWithStatus>;

export type ScheduledMessage = {
    id: string;
    content: string;
    contentHtml?: string;
    scheduledDate: Date;
    authorId: string;
    attachments?: MessageAttachment[];
};

export type GetDiscussionAttachmentsAPIResponse = {
    data: MessageAttachmentFromAPI[];
    pagination: PaginationFromAPI;
};

export type NormalizedSendingMessage = {
    [sendingMessageId: string]: SendingMessage | undefined;
};

export type Discussion = {
    messages: Message[];
    pagination: Pagination;
    isResponseRequired: boolean;
    draftMessage?: string;
    sendingMessage?: NormalizedSendingMessage;
    scheduledMessage?: ScheduledMessage | null;
};

export type DiscussionItem = Message | PhoneCall;

export type DiscussionIsResponseRequiredState =
    | 'TO_MARK_AS_NO_RESPONSE_REQUIRED'
    | 'MANUALLY_MARKED_AS_NO_RESPONSE_REQUIRED'
    | 'AUTOMATICALLY_MARKED_AS_NO_RESPONSE_REQUIRED';

export type NoResponseRequiredReason = 'no_response_required' | 'external_answer_done';

export type NormalizedDiscussion = {
    [dossierId in DossierId]: Discussion;
};

export type MessagesGroupByDate = {
    [date: string]: DiscussionItem[];
};

export type DisplayDiscussion = {
    messages: MessagesGroupByDate;
    isResponseRequiredState: DiscussionIsResponseRequiredState;
    draftMessage?: string;
    sendingMessage?: NormalizedSendingMessage;
    scheduledMessage?: ScheduledMessage | null;
};

export type SendMessageAPIResponse = { messageId: number };

export type UploadAttachmentAPI = { id: number };

export type GetOneMessageAPIResponse = {
    message: MessageFromAPI;
};

export type DiscussionAPIResponse = {
    data: {
        [id: string]: MessageFromAPI;
    };
    pagination: PaginationFromAPI;
    discussion: { is_response_required: boolean };
};

export type ScheduleMessageAPIResponse = { id: string };

export type ScheduledMessagesAPIResponse = {
    id: string;
    content: string;
    content_html: string | undefined;
    scheduled_at: string;
    author_id: string;
    attachments: AttachmentFromAPI[];
};

export type GetDraftMessageApiResponse = {
    authorId: string;
    provenance: string;
    content: string;
    contentHtml: string;
    updatedAt: string;
};

export interface SendMessageRequestAction extends Action<typeof actions.sendMessageRequestAction> {
    dossierId: DossierId;
    content: string;
    sendingMessageId: string;
    contentHtml?: string;
    attachmentIds?: string[];
}

export interface SendMessageSuccessAction extends Action<typeof actions.sendMessageSuccessAction> {
    messageId: number;
    dossierId: DossierId;
    sendingMessageId: string;
}

export interface SendMessageFailureAction extends Action<typeof actions.sendMessageFailureAction> {
    error: string;
    dossierId: DossierId;
    sendingMessageId: string;
}

export interface UpdateMessageRequestAction extends Action<
    typeof actions.updateMessageRequestAction
> {
    messageId: number;
    dossierId: DossierId;
    content: string;
    isScheduledMessage: boolean;
}

export interface UpdateMessageFailureAction extends Action<
    typeof actions.updateMessageFailureAction
> {
    messageId: number;
    dossierId: DossierId;
    error: string;
}

export interface GetMessageRequestAction extends Action<typeof actions.getMessageRequestAction> {
    dossierId: DossierId;
    messageId: number;
    sendingMessageId?: string;
}

export interface GetMessageSuccessAction extends Action<typeof actions.getMessageSuccessAction> {
    dossierId: DossierId;
    message: Message;
    sendingMessageId?: string;
}

export interface GetMessageFailureAction extends Action<typeof actions.getMessageFailureAction> {
    dossierId: DossierId;
    error: string;
}

export interface MarkMessageAsReadRequestAction extends Action<
    typeof actions.markMessageAsReadRequestAction
> {
    dossierId: DossierId;
    messageId: number;
}

export interface MarkMessageAsReadSuccessAction extends Action<
    typeof actions.markMessageAsReadSuccessAction
> {
    dossierId: DossierId;
}

export interface MarkMessageAsReadFailureAction extends Action<
    typeof actions.markMessageAsReadFailureAction
> {
    error: string;
}

export interface GetDraftMessageRequestAction extends Action<
    typeof actions.getDraftMessageRequestAction
> {
    dossierId: DossierId;
}

export interface GetDraftMessageSuccessAction extends Action<
    typeof actions.getDraftMessageSuccessAction
> {
    dossierId: DossierId;
    draftMessage: string;
    htmlDraftMessage: string;
}

export interface GetDraftMessageFailureAction extends Action<
    typeof actions.getDraftMessageFailureAction
> {
    error: string;
    dossierId: DossierId;
}

export interface SaveDraftMessageAction extends Action<typeof actions.saveDraftMessageAction> {
    dossierId: DossierId;
    draftMessage: string;
    htmlDraftMessage: string;
    metadata?: DraftMessageMetadata;
}

export interface SaveDraftMessageSuccessAction extends Action<
    typeof actions.saveDraftMessageSuccessAction
> {
    dossierId: DossierId;
}

export interface SaveDraftMessageFailureAction extends Action<
    typeof actions.saveDraftMessageFailureAction
> {
    error: string;
    dossierId: DossierId;
}

export interface ScheduleMessageRequestAction extends Action<
    typeof actions.scheduleMessageRequestAction
> {
    dossierId: DossierId;
    content: string;
    contentHtml: string;
    scheduledDate: Date;
    authorId: string;
    sendingMessageId: string;
    attachmentIds?: string[];
}

export interface ScheduleMessageSuccessAction extends Action<
    typeof actions.scheduleMessageSuccessAction
> {
    id: string;
    dossierId: DossierId;
    content: string;
    contentHtml: string;
    scheduledDate: Date;
    authorId: string;
    sendingMessageId?: string;
}

export interface ScheduleMessageFailureAction extends Action<
    typeof actions.scheduleMessageFailureAction
> {
    error: string;
    dossierId: DossierId;
}

export interface FetchScheduledMessagesRequestAction extends Action<
    typeof actions.fetchScheduledMessagesRequestAction
> {
    dossierId: DossierId;
}

export interface FetchScheduledMessagesSuccessAction extends Action<
    typeof actions.fetchScheduledMessagesSuccessAction
> {
    id: string;
    dossierId: DossierId;
    content: string;
    contentHtml?: string;
    scheduledDate: Date;
    authorId: string;
    attachments: MessageAttachment[];
}

export interface FetchScheduledMessagesFailureAction extends Action<
    typeof actions.fetchScheduledMessagesFailureAction
> {
    error: string;
    dossierId: DossierId;
}

export interface DeleteScheduledMessageRequestAction extends Action<
    typeof actions.deleteScheduledMessageRequestAction
> {
    dossierId: DossierId;
    scheduledMessageId: string;
}

export interface DeleteScheduledMessageSuccessAction extends Action<
    typeof actions.deleteScheduledMessageSuccessAction
> {
    dossierId: DossierId;
}

export interface DeleteScheduledMessageFailureAction extends Action<
    typeof actions.deleteScheduledMessageFailureAction
> {
    error: string;
    dossierId: DossierId;
}

export type DiscussionAction =
    | SendMessageRequestAction
    | SendMessageSuccessAction
    | SendMessageFailureAction
    | UpdateMessageRequestAction
    | UpdateMessageFailureAction
    | GetMessageRequestAction
    | GetMessageSuccessAction
    | GetMessageFailureAction
    | MarkMessageAsReadRequestAction
    | MarkMessageAsReadSuccessAction
    | MarkMessageAsReadFailureAction
    | GetDraftMessageRequestAction
    | GetDraftMessageSuccessAction
    | GetDraftMessageFailureAction
    | SaveDraftMessageAction
    | SaveDraftMessageSuccessAction
    | SaveDraftMessageFailureAction
    | ScheduleMessageRequestAction
    | ScheduleMessageSuccessAction
    | ScheduleMessageFailureAction
    | FetchScheduledMessagesRequestAction
    | FetchScheduledMessagesSuccessAction
    | FetchScheduledMessagesFailureAction
    | DeleteScheduledMessageRequestAction
    | DeleteScheduledMessageSuccessAction
    | DeleteScheduledMessageFailureAction;

// Reducer
export type SendingMessageStatus = {
    postContent?: APICallStatus;
};

export type NormalizedSendingMessageStatus = {
    [sendingMessageId: string]: SendingMessageStatus | undefined;
};

export type DiscussionStatusState = {
    [dossierId in DossierId]: {
        getPhoneCalls?: APICallStatus;
        getFirstPage?: APICallStatus;
        getDraftMessage?: APICallStatus;
        saveDraftMessage?: APICallStatus;
        updateMessage?: APICallStatus;
        sendingMessage: NormalizedSendingMessageStatus;
    };
};

export type DraftMessageMetadata = {
    origin: 'phone_call_summary' | null;
    phoneCallId?: number;
    originalContent?: string;
};

export type DiscussionEntitiesState = {
    [dossierId in DossierId]: {
        isResponseRequired: boolean;
        draftMessage?: string;
        htmlDraftMessage?: string;
        draftMessageMetadata?: DraftMessageMetadata;
        sendingMessage?: NormalizedSendingMessage;
        scheduledMessage?: ScheduledMessage | null;
    };
};
